// TODO: Overhaul — delete at cut-over. Serves the entity's investment-orders list (nested offers = the queue)
// and the per-offer /sell/details detail from fixtures until the real endpoints are served.
import { Client } from 'openapi-fetch';

import { ok, paginate } from '@views/investor/queries/overhaul-mock.util';

import {
  SquirrelMoneyCoreEnumerationsInvestmentAccountTypes as InvestmentAccountTypes,
  SquirrelMoneyContractsTypesInvestmentClasses as InvestmentClasses,
  SquirrelMoneyCoreContractsInvestmentSellOfferDto as InvestmentSellOfferDto,
  SquirrelMoneyCoreEnumerationsOfferReasons as OfferReasons,
  SquirrelMoneyCoreEnumerationsOfferStatuses as OfferStatuses,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';

/** Two orders, each carrying one offer — the queue. An offer's status reflects any recorded decision (so an
 *  accepted / declined offer drops out of the pending queue on the next read), otherwise Pending. */
function buildOrders(decisions: Map<number, OfferStatuses>) {
  const now = new Date().toISOString();
  const offer = (investmentOfferId: number, investmentOrderId: number) => ({
    investmentOfferId,
    investmentOrderId,
    offerStatus: decisions.get(investmentOfferId) ?? OfferStatuses.Pending,
    createDateNzt: now,
  });
  return {
    investmentOrders: paginate([
      { investmentOrderId: 9001, offers: [offer(456, 9001)] },
      { investmentOrderId: 9002, offers: [offer(457, 9002)] },
    ]),
  };
}

/** Per-offer /sell/details fixtures, keyed by offer id, covering the reachable disclosure combinations. */
function sellDetailsByOfferId(): Record<number, InvestmentSellOfferDto> {
  const expiry = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString();
  return {
    // Emergency Fund — interest rate below the prevailing rate; nothing else.
    456: {
      offer: {
        investmentOfferId: 456,
        investmentOrderId: 9001,
        amount: 2500,
        interestRate: 6,
        remainingTerm: 22,
        expiryDateNzt: expiry,
        offerStatus: OfferStatuses.Pending,
        offerReasons: OfferReasons.PrevailingRate,
      },
      prevailingInterestRate: 7.5,
      investmentAccount: {
        investmentAccountId: 501,
        displayName: 'Emergency Fund',
        investmentAccountType: InvestmentAccountTypes.TermInvestment,
        investmentClass: InvestmentClasses.ConsumerLoan,
      },
      loanInArrears: false,
      pastMaturity: false,
      showArrears: false,
      showRollover: false,
    },
    // House Deposit — in arrears with hardship, past maturity, and rolling over.
    457: {
      offer: {
        investmentOfferId: 457,
        investmentOrderId: 9002,
        amount: 5000,
        interestRate: 5.25,
        remainingTerm: 0,
        originalLoanTerm: 24,
        expiryDateNzt: expiry,
        offerStatus: OfferStatuses.Pending,
        offerReasons: OfferReasons.CreditEvent | OfferReasons.PrevailingRate | OfferReasons.PastMaturity,
      },
      prevailingInterestRate: 6,
      investmentAccount: {
        investmentAccountId: 502,
        displayName: 'House Deposit',
        investmentAccountType: InvestmentAccountTypes.TermInvestment,
        investmentClass: InvestmentClasses.ResidentialMortgage,
      },
      loanInArrears: true,
      loanMissedPayments: 3,
      daysInArrears: 21,
      hasHadHardship: true,
      showArrears: true,
      pastMaturity: true,
      daysPastMaturity: 45,
      showRollover: true,
      loanRollover: { rolloverDateNzt: expiry, term: 12, rateCard: { investorInterestRate: 5.5 } },
    },
  };
}

/**
 * Mock `ACCOUNT_OVERHAUL_CLIENT` fragment: serves the orders list (GET), the per-offer sell detail (GET
 * `…/sell/details`), and records accept / decline (POST) so a later read reflects the decision.
 */
export function createOrdersMockClient(): Client<accountOverhaulPaths> {
  const details = sellDetailsByOfferId();
  // In-memory decision store, keyed by offer id, so an accepted / declined offer stays actioned across modal
  // reopens within the session (the real BE persists this). Seeded empty — every offer starts Pending.
  const decisions = new Map<number, OfferStatuses>();
  return {
    GET(path: string, init?: unknown) {
      if (path.includes('/sell/details')) {
        const offerId = (init as { params?: { path?: { investmentOfferId?: number } } })?.params?.path
          ?.investmentOfferId;
        return Promise.resolve(ok(offerId != null ? details[offerId] : undefined));
      }
      return Promise.resolve(ok(buildOrders(decisions)));
    },
    POST(path: string, init?: unknown) {
      const offerId = (init as { params?: { path?: { investmentOfferId?: number } } })?.params?.path?.investmentOfferId;
      if (offerId != null) {
        if (path.endsWith('/confirm')) decisions.set(offerId, OfferStatuses.Accepted);
        else if (path.endsWith('/decline')) decisions.set(offerId, OfferStatuses.Declined);
      }
      return Promise.resolve(ok(undefined));
    },
  } as unknown as Client<accountOverhaulPaths>;
}
